import { useState } from 'react';

import type { WalletRecipientView } from '@/api/address-book-bff/types';
import { SendStatus } from '@/shared/modules/wallet/send/send-state';
import { CryptoAsset } from '@/shared/modules/wallet/use-crypto-assets';

import type { ConfirmationRecipient } from './use-send-flow';
import type { useSendSubmission } from './use-send-submission';

type SendSubmission = ReturnType<typeof useSendSubmission>;

export const useSendConfirmation = (submission: SendSubmission) => {
  const [recipient, setRecipient] = useState<ConfirmationRecipient | null>(null);

  const confirm = (address: string | undefined, identity: WalletRecipientView | null) => {
    if (address) {
      setRecipient({ address, identity: identity ? structuredClone(identity) : null });
    }
  };

  const send = (coin: CryptoAsset | undefined, amount: bigint | null) => {
    if (!coin || !recipient || amount === null) {
      return;
    }

    submission.submit({
      coinType: coin.coinType,
      decimals: coin.decimals,
      amount,
      recipient: recipient.address,
    });
  };

  const retry = () => {
    if (submission.state.status === SendStatus.UNCERTAIN) {
      submission.checkPendingTransfer();
      return;
    }

    submission.restart();
    setRecipient(null);
  };

  return {
    confirmationRecipient: recipient,
    confirming: recipient !== null,
    confirm,
    send,
    retry,
    cancel: () => setRecipient(null),
  };
};
